import React from 'react';
import { Link } from 'react-router-dom';
import { Truck, Shield, Users, Target, Award, CheckCircle } from 'lucide-react';

const AboutPage = () => {
  return (
    <div className="min-h-screen bg-[#0a0c10] pt-16">
      {/* Hero Section */}
      <div className="bg-gradient-to-b from-[#4ADE80]/10 to-transparent py-16">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4 font-['Barlow_Condensed'] uppercase tracking-wide">
            About <span className="text-[#4ADE80]">HaulLogix</span>
          </h1>
          <p className="text-xl text-gray-400 max-w-2xl mx-auto">
            The premium, verified loadboard built by auto transport professionals, for auto transport professionals.
          </p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-12">
        {/* Mission Section */}
        <section className="mb-16">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-lg bg-[#4ADE80]/20 flex items-center justify-center">
              <Target className="w-5 h-5 text-[#4ADE80]" />
            </div>
            <h2 className="text-2xl font-bold text-white">Our Mission</h2>
          </div>
          <div className="bg-[#12151a] rounded-xl border border-[#1a1d24] p-8">
            <p className="text-gray-300 text-lg leading-relaxed">
              At HaulLogix, we're on a mission to transform the auto transport industry by creating a 
              trustworthy, efficient, and professional platform that connects verified shippers, brokers, 
              and carriers. We believe that every load should be verified, every carrier should be vetted, 
              and every transaction should be transparent.
            </p>
            <p className="text-[#4ADE80] font-semibold mt-4 text-lg">
              "If it's not verified, it's not HaulLogix."
            </p>
          </div>
        </section>

        {/* Story Section */}
        <section className="mb-16">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-lg bg-[#4ADE80]/20 flex items-center justify-center">
              <Truck className="w-5 h-5 text-[#4ADE80]" />
            </div>
            <h2 className="text-2xl font-bold text-white">Our Story</h2>
          </div>
          <div className="space-y-4 text-gray-300 leading-relaxed">
            <p>
              HaulLogix was founded in Detroit, Michigan—the heart of America's automotive industry. 
              Our founders saw firsthand the challenges facing the auto transport sector: fraudulent 
              brokers, unreliable carriers, double-brokering, and a lack of transparency that cost 
              honest professionals time and money.
            </p>
            <p>
              We built HaulLogix to solve these problems. Our platform combines cutting-edge technology 
              with industry expertise to create a loadboard that prioritizes verification, trust, and 
              professional standards.
            </p>
            <p>
              From our Verified Load Chain technology to our comprehensive Fraud Shield system, every 
              feature we build is designed to protect our community and elevate the industry.
            </p>
          </div>
        </section>

        {/* Values Section */}
        <section className="mb-16">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-lg bg-[#4ADE80]/20 flex items-center justify-center">
              <Award className="w-5 h-5 text-[#4ADE80]" />
            </div>
            <h2 className="text-2xl font-bold text-white">Our Values</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              { title: 'Trust & Verification', desc: 'Every user, every load, every transaction is verified.' },
              { title: 'Transparency', desc: 'Clear pricing, honest communication, no hidden fees.' },
              { title: 'Community', desc: 'We support and protect our network of professionals.' },
              { title: 'Innovation', desc: 'Constantly improving with technology that serves you.' },
            ].map((value, i) => (
              <div key={i} className="bg-[#12151a] rounded-lg border border-[#1a1d24] p-6">
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle className="w-5 h-5 text-[#4ADE80]" />
                  <h3 className="font-semibold text-white">{value.title}</h3>
                </div>
                <p className="text-gray-400 text-sm">{value.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Team Section */}
        <section className="mb-16">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-lg bg-[#4ADE80]/20 flex items-center justify-center">
              <Users className="w-5 h-5 text-[#4ADE80]" />
            </div>
            <h2 className="text-2xl font-bold text-white">Our Team</h2>
          </div>
          <div className="bg-[#12151a] rounded-xl border border-[#1a1d24] p-8">
            <p className="text-gray-300 leading-relaxed">
              Our team combines decades of experience in auto transport, logistics technology, and 
              software development. We've hauled loads, managed dispatch operations, and built 
              enterprise software—giving us unique insight into what the industry truly needs.
            </p>
            <p className="text-gray-300 leading-relaxed mt-4">
              Based in Detroit, Michigan, we're proud to serve the hardworking men and women who 
              keep America's vehicles moving.
            </p>
          </div>
        </section>

        {/* CTA Section */}
        <section className="text-center bg-gradient-to-r from-[#4ADE80]/10 via-[#4ADE80]/5 to-[#4ADE80]/10 rounded-xl border border-[#4ADE80]/20 p-8">
          <Shield className="w-12 h-12 text-[#4ADE80] mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-white mb-2">Ready to Join?</h2>
          <p className="text-gray-400 mb-6">
            Experience the difference of a verified loadboard.
          </p>
          <div className="flex gap-4 justify-center">
            <Link 
              to="/register" 
              className="px-6 py-3 bg-[#4ADE80] hover:bg-[#22C55E] text-black font-semibold rounded-lg transition-colors"
            >
              Get Started
            </Link>
            <Link 
              to="/contact" 
              className="px-6 py-3 border border-[#1a1d24] hover:border-[#4ADE80] text-white rounded-lg transition-colors"
            >
              Contact Us
            </Link>
          </div>
        </section>
      </div>
    </div>
  );
};

export default AboutPage;
